export type EffectiveSubscription = {
  status: 'active' | 'past_due' | 'suspended' | 'cancelled' | 'trial' | 'unknown';
  graceEnd?: string | null;
  suspendedSince?: string | null;
  trialEndsAt?: string | null;
  billingStartsAt?: string | null;
  source: 'test_override' | 'live_payload' | 'lkg_cache' | 'unknown';
  locked: boolean;
  reason?: string;
};

const normalizeStatus = (value?: string | null) => (value || '').toLowerCase();

const mapDomainStatus = (status?: string | null): EffectiveSubscription['status'] => {
  const normalized = normalizeStatus(status);
  if (normalized === 'active') return 'active';
  if (['payment_needed', 'payment_incomplete', 'pending_payment'].includes(normalized)) return 'past_due';
  if (['cancelled', 'canceled', 'deleted'].includes(normalized)) return 'cancelled';
  if (normalized === 'suspended') return 'suspended';
  if (normalized === 'pending' || normalized === 'trial') return 'trial';
  return 'unknown';
};

const mapEmulationStatus = (status?: string | null): EffectiveSubscription['status'] => {
  const normalized = normalizeStatus(status);
  if (!normalized || normalized === 'none') return 'unknown';
  if (['active', 'past_due', 'suspended', 'cancelled', 'trial'].includes(normalized)) {
    return normalized as EffectiveSubscription['status'];
  }
  return 'unknown';
};

export const resolveEffectiveSubscription = (params: {
  domainStatus?: string | null;
  isTestUser?: boolean | null;
  emulationStatus?: string | null;
  graceEnd?: string | null;
  suspendedSince?: string | null;
  trialEndsAt?: string | null;
  billingStartsAt?: string | null;
  lkgStatus?: { status?: string; graceEnd?: string | null; suspendedSince?: string | null } | null;
}): EffectiveSubscription => {
  const isTestUser = Boolean(params.isTestUser);
  const emulationStatus = mapEmulationStatus(params.emulationStatus);
  const hasEmulation = isTestUser && emulationStatus !== 'unknown';

  if (hasEmulation) {
    return {
      status: emulationStatus,
      graceEnd: params.graceEnd ?? null,
      suspendedSince: params.suspendedSince ?? null,
      trialEndsAt: params.trialEndsAt ?? null,
      billingStartsAt: params.billingStartsAt ?? null,
      source: 'test_override',
      locked: emulationStatus !== 'active',
    };
  }

  const mapped = mapDomainStatus(params.domainStatus ?? null);
  if (mapped !== 'unknown') {
    return {
      status: mapped,
      trialEndsAt: params.trialEndsAt ?? null,
      billingStartsAt: params.billingStartsAt ?? null,
      source: 'live_payload',
      locked: mapped !== 'active' && mapped !== 'trial',
    };
  }

  if (params.lkgStatus?.status) {
    const lkgMapped = mapDomainStatus(params.lkgStatus.status);
    return {
      status: lkgMapped,
      graceEnd: params.lkgStatus.graceEnd ?? null,
      suspendedSince: params.lkgStatus.suspendedSince ?? null,
      trialEndsAt: params.trialEndsAt ?? null,
      billingStartsAt: params.billingStartsAt ?? null,
      source: 'lkg_cache',
      locked: lkgMapped !== 'active' && lkgMapped !== 'trial',
    };
  }

  return {
    status: 'unknown',
    source: 'unknown',
    locked: true,
    reason: 'no_status',
  };
};
